import { useState } from "react";
import {
  Bell,
  Check,
  Info,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  X,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { formatDistanceToNow } from "date-fns";
import { cn } from "../utils/utils";

export type NotificationType = "info" | "warning" | "success" | "error";

export interface Notification {
  id: string;
  title: string;
  description?: string;
  type: NotificationType;
  read: boolean;
  timestamp: Date;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Sample notifications data
/* const initialNotifications: Notification[] = [
  {
    id: "1",
    title: "New sales report available",
    description: "Q2 sales report is now available for download",
    type: "info",
    read: false,
    timestamp: new Date(Date.now() - 1000 * 60 * 15), 
    action: {
      label: "View",
      onClick: () => console.log("View sales report"),
    },
  },
  {
    id: "2",
    title: "System update scheduled",
    description: "A system update is scheduled for tonight at 2:00 AM",
    type: "warning",
    read: false,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2), 
  },
  {
    id: "3",
    title: "New customer sign-up",
    description: "Nike Inc. has created a new account",
    type: "success",
    read: true,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 5),
    action: {
      label: "View",
      onClick: () => console.log("View customer"),
    },
  },
  {
    id: "4",
    title: "Payment failed",
    description: "The payment for invoice #12345 has failed",
    type: "error",
    read: true,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24),
    action: {
      label: "Retry",
      onClick: () => console.log("Retry payment"),
    },
  },
  {
    id: "5",
    title: "Inventory alert",
    description: "Product SKU-12345 is running low on stock",
    type: "warning",
    read: true,
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 48),
  },
]; */

export function NotificationsDropdown() {
  const [notifications, setNotifications] =
    useState<Notification[]>([]);
  const [open, setOpen] = useState(false);

  const unreadCount = notifications.filter(
    (notification) => !notification.read
  ).length;

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications((prev) =>
      prev.map((notification) => ({ ...notification, read: true }))
    );
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case "info":
        return <Info className="h-4 w-4 text-blue-500" />;
      case "warning":
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case "success":
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case "error":
        return <XCircle className="h-4 w-4 text-red-500" />;
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative dark:text-white bg-white/60 dark:bg-black/60"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-black text-xs font-bold text-white dark:bg-white dark:text-black">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          <span className="sr-only">Notifications</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px]">
        <div className="flex items-center justify-between p-4">
          <DropdownMenuLabel className="text-base">
            Notifications
          </DropdownMenuLabel>
          <div className="flex gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="h-8 text-xs"
              >
                <Check className="mr-1 h-3 w-3" />
                Mark all as read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAll}
                className="h-8 text-xs"
              >
                <X className="mr-1 h-3 w-3" />
                Clear all
              </Button>
            )}
          </div>
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-[300px] overflow-y-auto">
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  "flex gap-3 p-4 hover:bg-muted/50",
                  !notification.read && "bg-muted/30",
                  notifications.indexOf(notification) !==
                    notifications.length - 1 && "border-b"
                )}
                onClick={() => markAsRead(notification.id)}
              >
                <div className="mt-1 flex-shrink-0">
                  {getIcon(notification.type)}
                </div>
                <div className="flex-1 space-y-1">
                  <div className="flex items-start justify-between">
                    <p
                      className={cn(
                        "text-sm font-medium",
                        !notification.read && "font-semibold"
                      )}
                    >
                      {notification.title}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatDistanceToNow(notification.timestamp, {
                          addSuffix: true,
                        })}
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-3 w-3" />
                            <span className="sr-only">More options</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-[160px]">
                          <DropdownMenuItem
                            onClick={() => markAsRead(notification.id)}
                          >
                            Mark as {notification.read ? "unread" : "read"}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => removeNotification(notification.id)}
                          >
                            Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  {notification.description && (
                    <p className="text-xs text-muted-foreground">
                      {notification.description}
                    </p>
                  )}
                  {notification.action && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        notification.action?.onClick();
                        markAsRead(notification.id);
                      }}
                    >
                      {notification.action.label}
                    </Button>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Bell className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-2 text-sm font-medium">No notifications</p>
              <p className="text-xs text-muted-foreground">
                You're all caught up!
              </p>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
